import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Package, Calendar, Clock, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!user?.is_admin) {
      navigate('/dashboard');
      toast.error('Accès refusé');
      return;
    }
    fetchData();
  }, [isAuthenticated, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, reportsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/users?limit=100'),
        api.get('/admin/reports'),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, verified) => {
    try {
      await api.put(`/admin/users/${userId}/verify?verified=${verified}`);
      toast.success(verified ? 'Utilisateur vérifié' : 'Vérification retirée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleReportStatus = async (reportId, status) => {
    try {
      await api.put(`/admin/reports/${reportId}?status=${status}`);
      toast.success('Signalement mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Administration</h1>
          <p className="text-muted-foreground">Gestion de la plateforme Oknî</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.users_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Utilisateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.services_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Services actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.completed_bookings || 0}</p>
                  <p className="text-sm text-muted-foreground">Échanges terminés</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_credits_exchanged || 0}</p>
                  <p className="text-sm text-muted-foreground">Crédits échangés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 rounded-xl">
            <TabsTrigger value="users">Utilisateurs</TabsTrigger>
            <TabsTrigger value="reports">
              Signalements
              {reports.filter((r) => r.status === 'pending').length > 0 && (
                <Badge className="ml-2 bg-destructive" variant="destructive">
                  {reports.filter((r) => r.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Users */}
          <TabsContent value="users">
            <Card className="rounded-xl">
              <CardHeader>
                <CardTitle>Gestion des utilisateurs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-4 border rounded-xl">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{u.full_name}</span>
                          {u.verified && (
                            <CheckCircle className="w-4 h-4 text-primary" />
                          )}
                          {u.is_admin && (
                            <Badge variant="secondary">Admin</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                        <p className="text-sm text-muted-foreground">
                          {u.credits_balance} crédits • {u.total_exchanges} échanges
                        </p>
                      </div>
                      <Button
                        variant={u.verified ? 'outline' : 'default'}
                        size="sm"
                        className="rounded-full"
                        onClick={() => handleVerifyUser(u.id, !u.verified)}
                      >
                        {u.verified ? 'Retirer vérification' : 'Vérifier'}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports">
            <Card className="rounded-xl">
              <CardHeader>
                <CardTitle>Signalements</CardTitle>
              </CardHeader>
              <CardContent>
                {reports.length > 0 ? (
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <div key={report.id} className="p-4 border rounded-xl">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-destructive" />
                              <Badge variant={report.status === 'pending' ? 'destructive' : 'secondary'}>
                                {report.status === 'pending' ? 'En attente' : 
                                 report.status === 'reviewed' ? 'Examiné' : 'Résolu'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {format(new Date(report.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                            </p>
                            <p className="font-medium">Raison : {report.reason}</p>
                          </div>
                          {report.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReportStatus(report.id, 'resolved')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Résoudre
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Aucun signalement
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
